// React and routing
import { Box, Button, CircularProgress, Container, Divider, Grid2 as Grid, Typography } from '@mui/material'
import { useAtomValue } from 'jotai'
import * as React from 'react'
import { useNavigate } from 'react-router'

import GroupSessionCard from '../components/cards/GroupSessionCard'
import TripCard from '../components/cards/TripCard'
import EmptyStateCard from '../components/common/EmptyStateCard'
import SimpleHorizontalCards from '../components/layout/SimpleHorizontalCards'
import { useGroupSession } from '../contexts/GroupSessionContext'
import { useTrip } from '../contexts/TripContext'
import { isAdminAtom } from '../domains/auth/AuthSelectors'
import { isInstructorAtom } from '../domains/auth/AuthSelectors'

export default function Events() {
  const navigate = useNavigate()
  const isAdmin = useAtomValue(isAdminAtom)
  const isInstructor = useAtomValue(isInstructorAtom)
  const [pageInited, setPageInited] = React.useState(false)

  const { upcomingGroupSessions: sessions, fetchUpComingGroupSessions, upcomingGroupSessionsLoaded } = useGroupSession()
  const { upcomingTrips: trips, fetchUpcomingTrips, upcomingTripsLoaded } = useTrip()

  React.useEffect(() => {
    fetchUpComingGroupSessions()
  }, [fetchUpComingGroupSessions])

  React.useEffect(() => {
    fetchUpcomingTrips()
  }, [fetchUpcomingTrips])

  const sessionChangeHook = async () => {
    fetchUpComingGroupSessions()
  }

  const tripChangeHook = async () => {
    fetchUpcomingTrips()
  }

  React.useEffect(() => {
    if (upcomingGroupSessionsLoaded && upcomingTripsLoaded) {
      setPageInited(true)
    }
  }, [upcomingGroupSessionsLoaded, upcomingTripsLoaded])

  if (!pageInited) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container sx={{ maxWidth: '1400px' }}>
      <Grid alignItems="center" justifyContent="center" sx={{ my: 4 }}>
        {/* Group Sessions Section */}
        <Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4">Upcoming Sessions</Typography>
            {sessions.length > 0 && (
              <Button variant="outlined" size="small" onClick={() => navigate('/events/sessions')}>
                View All Sessions
              </Button>
            )}
          </Box>

          {sessions.length > 0 ? (
            <SimpleHorizontalCards onShowAll={() => navigate('/events/sessions')}>
              {sessions.map((session) => {
                return <GroupSessionCard key={session.resourceId} session={session} mode="card" updateHook={sessionChangeHook} deleteHook={sessionChangeHook} />
              })}
            </SimpleHorizontalCards>
          ) : (
            <EmptyStateCard
              title="No Sessions Available Right Now"
              subtitle={
                isInstructor
                  ? 'Currently there are no active sessions. Share your expertise by creating a new session - help others discover the joy of winter sports!'
                  : "We don't have any sessions scheduled at the moment. Our instructors are preparing new classes - check back soon for exciting learning opportunities!"
              }
              buttonText="Create New Group Session"
              onButtonClick={() => navigate('/group-session/create')}
              showButton={isInstructor || isAdmin || false}
              image="https://images.pexels.com/photos/848612/pexels-photo-848612.jpeg"
            />
          )}
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Trips Section */}
        <Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4">Upcoming Trips</Typography>
            {trips.length > 0 && (
              <Button variant="outlined" size="small" onClick={() => navigate('/events/trips')}>
                View All Trips
              </Button>
            )}
          </Box>

          {trips.length > 0 ? (
            <SimpleHorizontalCards onShowAll={() => navigate('/events/trips')}>
              {trips.map((trip) => {
                return <TripCard key={trip.resourceId} trip={trip} mode="card" updateHook={tripChangeHook} deleteHook={tripChangeHook} />
              })}
            </SimpleHorizontalCards>
          ) : (
            <EmptyStateCard
              title="Group Trips Feature Coming Soon!"
              subtitle={
                isAdmin
                  ? "We're working hard to bring group trips feature to our platform. Stay tuned for updates!"
                  : "Exciting news! Group ski trips feature is in development. Soon you'll be able to join amazing winter adventures with fellow snow enthusiasts!"
              }
              buttonText={isAdmin ? 'Coming Soon' : null}
              onButtonClick={() => navigate('/wip')}
              showButton={isAdmin || false}
              image="https://images.pexels.com/photos/848599/pexels-photo-848599.jpeg"
            />
          )}
        </Grid>
      </Grid>
    </Container>
  )
}
